import {
  Avatar,
  ButtonGroup,
  Divider,
  ListItemIcon,
  MenuItem,
  MenuList,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Badge from "@mui/material/Badge";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import BasicPopover from "components/BasicPopover";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import NotificationsMenu from "components/Notifications";
import useNotificationFeed from "components/Notifications/useNotificationFeed";
import {
  setMiniSidenav,
  setOpenConfigurator,
  setTransparentNavbar,
  useAuth,
  useMaterialUIController,
} from "contexts/auth.context";
import { useLanguage } from "contexts/language.context";
import Breadcrumbs from "examples/Breadcrumbs";
import {
  navbar,
  navbarContainer,
  navbarRow,
} from "examples/Navbars/DashboardNavbar/styles";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "services/auth-service";

export const URL_TO_LABEL_CODE = {
  audits: "audit-plural",
  calendar: "calendar",
  tasks: "task-plural",
  accounts: "account-plural",
  branches: "branch-plural",
  templates: "template-plural",
  pillars: "pillar-plural",
  "summary-groups": "summary-group-plural",
  subscriptions: "subscription-plural",
  colorPalettes: "color-palette-plural",
  categories: "category-plural",
  roles: "role-plural",
  languages: "language-plural",
  translations: "translation-plural",
  countries: "country-plural",
  territories: "territory-plural",
  regions: "region-plural",
  users: "user-plural",
  profile: "profile",
  departments: "department-plural",
  sections: "competency-plural",
  sheets: "sheet-plural",
  "sheet-templates": "sheet-template-plural",
  "sheet-template-categories": "template-category-plural",
  "logsheet-entities": "entity-plural",
  "entity-categories": "entity-category-plural",
  "sheet-entity-elements": "entity-element-plural",
  "sheet-templates": "sheet-template-plural",
  "sheet-properties": "property-plural",
  units: "unit-plural",
};

function DashboardNavbar({ absolute, light, isMini, customRoute }) {
  const authContext = useAuth();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
  } = controller;
  const [open, setOpen] = useState({});
  const { label } = useLanguage();
  const route = useLocation()
    .pathname.split("/")
    .slice(1)
    .map((v) =>
      URL_TO_LABEL_CODE[v.toLowerCase()]
        ? label(URL_TO_LABEL_CODE[v.toLowerCase()]) || v
        : v,
    );
  const notificationFeed = useNotificationFeed();
  const { unreadCount } = notificationFeed;
  const { changeLanguage, languages, language } = useLanguage();
  let navigate = useNavigate();

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar,
      );
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);


    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener("scroll", handleTransparentNavbar);
    };
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) =>
    setOpen({ ...open, notifications: event.currentTarget });
  const handleCloseMenu = () => setOpen({ ...open, notifications: false });

  const renderMenus = () => (
    <>
      <NotificationsMenu
        anchorElement={open.notifications}
        onClose={handleCloseMenu}
        {...notificationFeed}
      />
      <BasicPopover
        button={
          <MDButton
            onClick={(e) => {
              setOpen({ ...open, profile: e.currentTarget });
            }}
            variant="outlined"
            color="info"
            size="small"
            sx={(theme) => ({
              backgroundColor: "white !important",
              borderColor: "transparent" + " !important",
              boxShadow: "0px 2px 3px rgba(0, 0, 0, 0.15)",
              pl: 1,
            })}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
              }}
              src={authContext?.user?.profileImage}
            />
            <MDTypography
              sx={{
                fontSize: "0.9rem",
                fontWeight: "500",
                ml: 1,
              }}
            >
              {authContext?.user?.name}
            </MDTypography>
          </MDButton>
        }
        content={
          <MDBox sx={{ m: -1, backgroundColor: "white", minWidth: "300px" }}>
            <MenuList dense={true}>
              <MenuItem
                sx={{
                  mx: -1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "start",
                  gap: 1,
                  px: 3,
                  py: 1,
                }}
                onClick={() => {
                  navigate("/profile");
                }}
              >
                <MDBox
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                    }}
                    src={authContext?.user?.profileImage}
                  />
                  <MDBox sx={{ display: "flex", flexDirection: "column" }}>
                    <MDTypography sx={{ fontSize: "1rem", fontWeight: "500" }}>
                      {authContext?.user?.name}
                    </MDTypography>
                    <MDTypography sx={{ fontSize: "0.9rem" }}>
                      {authContext?.user?.email}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MenuItem>
              <Divider />
              <MenuItem
                size="small"
                sx={{ mx: -1 }}
                onClick={() => {
                  navigate("/profile");
                }}
              >
                <ListItemIcon>
                  <Icon fontSize="medium">settings</Icon>
                </ListItemIcon>
                <MDTypography sx={{ fontSize: "1rem" }}>
                  {label("setting-plural")}
                </MDTypography>
              </MenuItem>
              <Divider />
              <MenuItem size="small" sx={{ mx: -1 }} onClick={handleLogOut}>
                <ListItemIcon>
                  <Icon fontSize="medium">logout</Icon>
                </ListItemIcon>
                <MDTypography sx={{ fontSize: "1rem" }}>
                  {label("logout-button")}
                </MDTypography>
              </MenuItem>
            </MenuList>
          </MDBox>
        }
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              borderRadius: "12px",

              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
                borderRadius: "12px",
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 19,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      />
    </>
  );

  // Styles for the navbar icons
  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
    fontSize: "32px",
  });

  const handleLogOut = async () => {
    const response = await AuthService.logout();
    authContext.logout();
  };

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) =>
        navbar(theme, { transparentNavbar, absolute, light, darkMode })
      }
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox
          color="inherit"
          mb={{ xs: 1, md: 0 }}
          sx={(theme) => navbarRow(theme, { isMini })}
        >
          <Breadcrumbs
            icon="home"
            title={
              (customRoute && customRoute[customRoute?.length - 1]) ||
              route[route.length - 1]
            }
            route={customRoute || route}
            light={light}
          />
        </MDBox>

        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox
              display="flex"
              alignItems="center"
              color={light ? "white" : "inherit"}
            >
              <IconButton sx={{ mr: 2 }} onClick={() => handleMiniSidenav()}>
                <Icon>menu</Icon>
              </IconButton>

              <ButtonGroup size="small" sx={{ marginRight: 1 }}>
                {languages.map((lang) => {
                  return (
                    <MDButton
                      onClick={() => {
                        changeLanguage(lang.id);
                      }}
                      variant="contained"
                      color={language.id == lang.id ? "info" : null}
                      key={"Langg: " + lang.id}
                      size="small"
                      sx={{ padding: "0 !important" }}
                    >
                      {lang.id.toUpperCase()}
                    </MDButton>
                  );
                })}
              </ButtonGroup>

              <Badge
                sx={(theme) => ({
                  "& .MuiBadge-badge": {
                    right: 11,
                    top: 2,
                  },
                })}
                badgeContent={unreadCount}
                color="primary"
                invisible={!unreadCount}
              >
                <MDButton
                  iconOnly
                  variant={unreadCount ? "gradient" : "outlined"}
                  color="primary"
                  sx={(theme) => ({
                    backgroundColor: unreadCount
                      ? null
                      : "white !important",
                    borderColor: "transparent" + " !important",
                    boxShadow: "0px 2px 3px rgba(0, 0, 0, 0.15)",
                    color: unreadCount
                      ? "white"
                      : theme.palette.text.main,
                    mr: 1,
                  })}
                  size="medium"
                  onClick={handleOpenMenu}
                >
                  <Icon
                    fontSize="large"
                    sx={{
                      transform: "scale(1.4)",
                    }}
                  >
                    notifications
                  </Icon>
                </MDButton>
              </Badge>

              {renderMenus()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
